
import { defineComponent, PropType } from "vue";
import { mapActions, mapGetters, mapState } from "vuex";
import Datatable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";

import AddAttachmentDialog from "@/components/Attachments/AddAttachmentDialog.vue";
import ConfirmChangesDialog from "@/components/UI/ConfirmChangesDialog.vue";

import Attachment, { AttachmentUpload } from "@/types/attachment";

import Utils from "@/utility/utils";

export default defineComponent({
  components: {
    Datatable,
    Column,
    Button,
    AddAttachmentDialog,
    ConfirmChangesDialog,
  },
  props: {
    show: Boolean,
    showMail: {
      type: Boolean,
      default: false,
    },
    customerArrayIndex: Number,
    isLoadingAttachment: Boolean,
    isLogHidden: {
      type: Boolean,
      default: false,
    },
    record: Object,
    hostFile: String,
    hostId: String,
    showTotalAttachments: {
      type: Boolean,
      default: true,
    },
    allowTempAttachments: {
      type: Boolean,
      default: false,
    },
    tempAttachments: {
      type: Array as PropType<AttachmentUpload[]>,
    },
  },
  emits: [
    "hide",
    "onUploadAttachment",
    "onSaved",
    "onFetchRecords",
    "onDownload",
    "onDeleted",
    "onPageClicked",
    "onSaveTempAttachment",
  ],
  created() {
    if (this.validHostFile && this.attachmentRecords.length === 0) {
      this.fetchAttachmentRecords();
    }

    if (this.hostFile === "CUST") {
      if (
        ((this.record as any).attachments_items != null &&
          this.getAttachments(this.customerArrayIndex) == null) ||
        this.getAttachments(this.customerArrayIndex).length === 0
      ) {
        this.fetchAttachmentRecords(false);
      } else {
        this.attachmentRecords = this.getAttachments(
          this.customerArrayIndex || 0,
        );
      }
      this.first = this.getFirstRow(this.customerArrayIndex);
    }
    this.isAttachmentLogHidden = this.isLogHidden;
  },
  updated() {
    if (this.validHostFile && this.attachmentRecords.length === 0) {
      this.fetchAttachmentRecords();
    }

    if (this.allowTempAttachments && this.tempAttachments) {
      this.tempAttachmentRecords = this.tempAttachments;
    }
  },
  data() {
    return {
      isHidden: false,
      isAttachmentLogHidden: false,
      loadingAttachments: false,
      showAddTicketAttachmentDialog: false,
      showEmailAttachmentDialog: false,
      currentAttachmentId: "",
      fileName: "",
      attachmentRecords: [] as Array<Attachment>,
      loadingAttachmentIds: [] as Array<string>,
      deletingAttachmentIds: [] as Array<string>,
      recordObject: {},
      isUploading: false,
      rows: 5,
      first: 0,
      page: 1,
      currentId: -1,
      tempAttachmentRecords: [] as Array<AttachmentUpload>,
    };
  },
  computed: {
    ...mapState(["control"]),
    ...mapGetters({
      getAttachments: "attachments/getCustomerAttachments",
      getRangeEnd: "attachments/getCustomerAttachmentRangeEnd",
      getFolderNames: "control/getFolderNames",
      getClient: "session/getClient",
      getUser: "session/getUser",
      getFirstRow: "attachments/getCustomerAttachmentFirstRow",
    }),
    isAttachmentLogVisible(): boolean {
      if (this.hostFile === "CUST") return false;
      return this.isAttachmentLogHidden;
    },
    validHostFile(): boolean {
      return (
        this.hostFile === "SALEOPP" ||
        this.hostFile === "SOQUOTE" ||
        this.hostFile === "BILL" ||
        this.hostFile === "TICKET" ||
        this.hostFile === "AP" ||
        this.hostFile === "GLTRANS"
      );
    },
    totalAttachments(): number {
      return (
        this.tempAttachmentRecords.length || this.attachmentRecords.length || 0
      );
    },
  },
  methods: {
    ...mapActions({
      fetchControl: "control/fetchControl",
      postAttachment: "attachments/postAttachment",
      downloadAttachment: "attachments/downloadAttachment",
      deleteAttachment: "attachments/deleteAttachment",
      fetchAttachments: "attachments/fetchAttachments",
      addNotification: "notification/add",
      updatePage: "attachments/updatePage",
      setNextRow: "attachments/updateCustomerRowIndex",
    }),
    viewAttachmentsLog() {
      this.isAttachmentLogHidden = !this.isAttachmentLogHidden;
      
      // Refetch attachments when expanding the log to ensure fresh data
      if (!this.isAttachmentLogHidden && this.validHostFile) {
        this.fetchAttachmentRecords(false);
      }
    },
    showEmailModal({ id, file_name }: any) {
      this.showEmailAttachmentDialog = true;
      this.currentAttachmentId = id;
      this.fileName = file_name;
    },
    getDate(attachment: Attachment) {
      if (attachment && attachment.date_created) {
        return Utils.formatDate(attachment.date_created);
      } else {
        return "";
      }
    },
    downloadIcon(ticketId: string) {
      const downloading = this.loadingAttachmentIds.includes(ticketId) || false;

      if (!downloading) return "pi pi-download";
      return "pi pi-spin pi-spinner";
    },
    download({ id }: any) {
      this.loadingAttachmentIds.push(id);

      const record: Attachment = this.attachmentRecords?.find(
        (attachment: Attachment) => attachment.id === id,
      ) as Attachment;

      if (record == null) {
        this.addNotification({
          message: `Error downloading attachment`,
          type: "error",
        });
        return;
      }

      let mime_type = record?.mime_type;

      if (mime_type == null) mime_type = "text/plain";

      this.downloadAttachment({
        id,
        mime_type,
      })
        .then(() => {
          this.$emit("onDownload", { success: true, attachmentId: id });
        })
        .catch((error) => {
          this.$emit("onDownload", { success: false, error });
        })
        .finally(() => {
          this.removeDownloadingState(id);
        });
    },
    deleteIcon(ticketId: string) {
      const deletingRecord =
        this.deletingAttachmentIds.includes(ticketId) || false;

      if (!deletingRecord) return "pi pi-trash";
      return "pi pi-spin pi-spinner";
    },
    remove(id: string) {
      this.deletingAttachmentIds.push(id);

      this.deleteAttachment({
        id,
        host_file: this.hostFile,
        host_id: this.hostId
          ? this.hostId
          : this.hostFile === "CUST"
            ? (this.record as any).cust_id
            : (this.record as any).id,
        index: this.customerArrayIndex,
      })
        .then(() => {
          if (this.hostFile === "CUST") {
            this.attachmentRecords = this.getAttachments(
              this.customerArrayIndex,
            );
          }
          this.removeDeleteLoadingState(id);
          this.$emit("onDeleted", { success: true, attachmentId: id });
        })
        .catch(({ error }) => {
          this.removeDeleteLoadingState(id);
          this.$emit("onDeleted", { success: false, error });
        });
    },
    removeDeleteLoadingState(id: string) {
      const attachmentId = this.deletingAttachmentIds.indexOf(id);
      if (attachmentId > -1) {
        this.deletingAttachmentIds.splice(attachmentId, 1);
      }
    },
    removeDownloadingState(id: string) {
      const attachmentId = this.loadingAttachmentIds.indexOf(id);
      if (attachmentId > -1) {
        this.loadingAttachmentIds.splice(attachmentId, 1);
      }
    },
    saveAttachment(data: any) {
      const { desc, doc, doc_name, folder, mime_type } = data;

      if (this.allowTempAttachments && !this.hostId) {
        const encodedDoc = Utils.attachmentEncode(doc);
        this.tempAttachmentRecords.push({
          doc_name,
          doc: encodedDoc,
          mime_type,
          folder,
          host_file: this.hostFile,
          host_id: "TEMP",
          user_id: this.getUser.first_name.concat(" " + this.getUser.last_name),
          desc,
        });
        this.isUploading = false;
        this.showAddTicketAttachmentDialog = false;

        this.$emit("onSaveTempAttachment", {
          success: true,
          tempAttachmentRecords: this.tempAttachmentRecords,
        });
        return;
      }

      this.isUploading = true;
      const recordId = this.hostId
        ? this.hostId
        : this.hostFile === "CUST"
          ? (this.record as any).cust_id
          : (this.record as any).id;

      this.postAttachment({
        doc_name,
        doc,
        mime_type,
        folder,
        host_file: this.hostFile,
        host_id: recordId,
        user_id: this.getUser.first_name.concat(" " + this.getUser.last_name),
        desc,
        index: this.customerArrayIndex,
      })
        .then(({ attachment_id }) => {
          this.showAddTicketAttachmentDialog = false;
          if (this.hostFile === "CUST") {
            this.attachmentRecords = this.getAttachments(
              this.customerArrayIndex,
            );
          }

          this.$emit("onSaved", {
            success: true,
            attachmentId: attachment_id,
            recordId: recordId,
          });
          this.first = 0;
        })
        .catch((error) => {
          this.$emit("onSaved", { success: false, error });
          this.addNotification({
            message: `Error saving attachment`,
            type: "error",
          });
        })
        .finally(async () => {
          this.isUploading = false;
        });
    },
    async confirmDelete(id: string) {
      const res: boolean = await (this.$refs["confirmChanges"] as any).open();

      if (res) {
        // Check if this is a temporary attachment (before ticket is saved)
        if (this.tempAttachmentRecords.length > 0) {
          this.removeTempAttachment(id);
        } else {
          this.remove(id);
        }
      }
    },
    removeTempAttachment(id: string) {
      // For temp attachments, id is actually the doc_name
      const index = this.tempAttachmentRecords.findIndex(
        (attachment) => attachment.doc_name === id
      );
      
      if (index > -1) {
        this.tempAttachmentRecords.splice(index, 1);
        this.$emit("onSaveTempAttachment", {
          tempAttachmentRecords: this.tempAttachmentRecords,
        });
        this.addNotification({
          message: `Attachment removed`,
          type: "success",
        });
      }
    },
    async fetchAttachmentRecords(isAddingAttachments?: boolean) {
      const attachmentsRecords =
        (this.record as any)?.attachments_items != null
          ? [...(this.record as any)?.attachments_items]
          : ([] as Array<Attachment>);

      if (attachmentsRecords.length > 0) {
        this.loadingAttachments = true;
        this.fetchAttachments({
          record: this.record as any,
          index: this.customerArrayIndex ?? undefined,
          isAddingAttachments,
        })
          .then(({ attachments }) => {
            this.loadingAttachments = false;
            this.attachmentRecords = attachments;
            this.$emit("onFetchRecords", {
              success: true,
              totalRecords: this.totalAttachments,
            });
          })
          .catch((error) => {
            this.loadingAttachments = false;
            this.$emit("onFetchRecords", { success: false, error });
          });
        if (isAddingAttachments != null && !isAddingAttachments) {
          this.page = 1;
          this.first = 0;
        }
      } else {
        if (this.hostFile === "CUST") {
          this.attachmentRecords = this.getAttachments(
            this.customerArrayIndex || 0,
          );
        }
      }
    },
    handlePage(event: any) {
      if (this.hostFile != null && this.hostFile === "CUST") {
        this.$emit("onPageClicked", {
          pageNumber: event.page,
          firstPageIndex: event.first,
          pageCount: event.pageCount,
        });
        // this.updatePage({ pageNumber: event.page, index: this.index });
        // this.setNextRow({ row: event.first, index: this.index });
        if (
          (event.page == event.pageCount ||
            event.page == event.pageCount - 1 ||
            event.page == event.pageCount - 2) &&
          (this.getAttachments(this.customerArrayIndex)?.length || 0) ==
            this.getRangeEnd(this.customerArrayIndex) - 100
        ) {
          this.fetchAttachmentRecords(true);
        }
      }
    },
    resetAttachments() {
      this.attachmentRecords = [];
      this.tempAttachmentRecords = [];
      this.first = 0;
    },
  },
  watch: {
    show(newShow) {
      this.isHidden = newShow;
    },
    record(newRecord) {
      this.recordObject = newRecord;
      if (this.hostFile === "CUST") {
        if (
          this.getAttachments(this.customerArrayIndex) == null ||
          this.getAttachments(this.customerArrayIndex).length === 0
        ) {
          this.fetchAttachmentRecords(false);
        } else {
          this.attachmentRecords = this.getAttachments(this.customerArrayIndex);
        }
      }

      if (this.validHostFile && newRecord) {
        this.attachmentRecords = [];
        this.fetchAttachmentRecords();
      }
    },
  },
});
